import React from 'react';
import { Layers, Eye, EyeOff, Mountain, MousePointerClick, Type } from 'lucide-react';
import { getLayerStyle, getSortedLayerTypes } from './mapStyles';

export interface LayerInfo {
  type: string;
  count: number;
  has_entities: boolean;
}

interface LayerControlPanelProps {
  layers: LayerInfo[];
  visibleLayers: Set<string>;
  onToggleLayer: (layerType: string) => void;
  onToggleAll: (visible: boolean) => void;
  terrainEnabled: boolean;
  onToggleTerrain: () => void;
  labelsEnabled: boolean;
  onToggleLabels: () => void;
}

/** Format layer type slug for display: "ug_conduit_34.5kv" → "UG Conduit 34.5kV" */
function formatLayerName(layerType: string): string {
  return layerType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/34\.5kv/i, '34.5kV')
    .replace(/1ckt/i, '1-Ckt');
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

const LayerControlPanel: React.FC<LayerControlPanelProps> = ({
  layers,
  visibleLayers,
  onToggleLayer,
  onToggleAll,
  terrainEnabled,
  onToggleTerrain,
  labelsEnabled,
  onToggleLabels,
}) => {
  const allVisible = layers.every((l) => visibleLayers.has(l.type));
  const noneVisible = layers.every((l) => !visibleLayers.has(l.type));

  // Sort layers by render order for display consistency
  const sortedLayers = getSortedLayerTypes(layers.map((l) => l.type)).map(
    (type) => layers.find((l) => l.type === type)!
  );

  return (
    <div className="absolute top-3 right-3 z-10 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg w-64 max-h-[calc(100%-24px)] overflow-y-auto">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)]">
        <Layers size={16} className="text-[var(--color-text-secondary)]" />
        <span className="text-sm font-medium text-[var(--color-text-primary)]">Layers</span>
        <div className="ml-auto flex gap-1">
          <button
            onClick={() => onToggleAll(true)}
            className={`text-xs px-2 py-0.5 rounded ${allVisible ? 'bg-blue-600 text-white' : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'}`}
            title="Show all layers"
          >
            All
          </button>
          <button
            onClick={() => onToggleAll(false)}
            className={`text-xs px-2 py-0.5 rounded ${noneVisible ? 'bg-blue-600 text-white' : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'}`}
            title="Hide all layers"
          >
            None
          </button>
        </div>
      </div>

      <div className="p-2 space-y-0.5">
        {sortedLayers.map((layer) => {
          const style = getLayerStyle(layer.type);
          const visible = visibleLayers.has(layer.type);

          return (
            <button
              key={layer.type}
              onClick={() => onToggleLayer(layer.type)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-[var(--color-bg-secondary)] transition-colors"
            >
              <div
                className="w-3 h-3 rounded-sm flex-shrink-0 border border-black/20"
                style={{
                  backgroundColor: style.type === 'fill' ? style.color : 'transparent',
                  borderColor: style.type === 'line' ? style.color : undefined,
                  borderWidth: style.type === 'line' ? 2 : undefined,
                  opacity: visible ? 1 : 0.3,
                }}
              />
              <span
                className={`text-xs flex-1 truncate ${visible ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-tertiary)]'}`}
              >
                {formatLayerName(layer.type)}
              </span>
              {style.selectable && (
                <MousePointerClick size={10} className="text-amber-400 flex-shrink-0" title="Selectable" />
              )}
              {style.minZoom > 0 && (
                <span className="text-[9px] text-[var(--color-text-tertiary)] flex-shrink-0" title={`Visible at zoom ${style.minZoom}+`}>
                  z{style.minZoom}+
                </span>
              )}
              <span className="text-[10px] text-[var(--color-text-tertiary)] tabular-nums flex-shrink-0">
                {formatCount(layer.count)}
              </span>
              {visible ? (
                <Eye size={12} className="text-[var(--color-text-secondary)] flex-shrink-0" />
              ) : (
                <EyeOff size={12} className="text-[var(--color-text-tertiary)] flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      <div className="border-t border-[var(--color-border)] px-3 py-2 space-y-1">
        <button
          onClick={onToggleLabels}
          className="w-full flex items-center gap-2 text-xs hover:bg-[var(--color-bg-secondary)] rounded px-2 py-1.5 transition-colors"
        >
          <Type size={14} className={labelsEnabled ? 'text-blue-500' : 'text-[var(--color-text-tertiary)]'} />
          <span className={labelsEnabled ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-tertiary)]'}>
            Labels
          </span>
          <div className={`ml-auto w-7 h-4 rounded-full transition-colors ${labelsEnabled ? 'bg-blue-500' : 'bg-[var(--color-bg-tertiary)]'}`}>
            <div className={`w-3 h-3 rounded-full bg-white shadow-sm transition-transform mt-0.5 ${labelsEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
          </div>
        </button>
        <button
          onClick={onToggleTerrain}
          className="w-full flex items-center gap-2 text-xs hover:bg-[var(--color-bg-secondary)] rounded px-2 py-1.5 transition-colors"
        >
          <Mountain size={14} className={terrainEnabled ? 'text-blue-500' : 'text-[var(--color-text-tertiary)]'} />
          <span className={terrainEnabled ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-tertiary)]'}>
            3D Terrain
          </span>
          <div className={`ml-auto w-7 h-4 rounded-full transition-colors ${terrainEnabled ? 'bg-blue-500' : 'bg-[var(--color-bg-tertiary)]'}`}>
            <div className={`w-3 h-3 rounded-full bg-white shadow-sm transition-transform mt-0.5 ${terrainEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
          </div>
        </button>
      </div>
    </div>
  );
};

export default LayerControlPanel;
